/* global CodeMirror, editor  */
import { escapeCriticText } from '../critic-markup'
import { planCriticSuggestion } from '../critic-suggestion'
import { frontmatterRange } from '../critic-source'

const wrapSymbols = ['*', '_', '~', '^', '+', '=']

export function canInsertCritic (cm) {
  const source = cm.getValue()
  const header = frontmatterRange(source)
  if (!header) return true
  const blocked = cm.listSelections().some(range => {
    const from = cm.indexFromPos(range.from())
    // At EOF without a newline, appending markup would break the delimiter.
    return from < header[1] || (from === header[1] && !/[\r\n]$/.test(source.slice(0, from)))
  })
  if (blocked && window.showToast) window.showToast('Comments and suggestions belong below the YAML header.', true)
  return !blocked
}

export function wrapTextWith (editor, cm, symbol) {
  if (!cm.getSelection()) {
    return CodeMirror.Pass
  } else {
    const ranges = cm.listSelections()
    for (let i = 0; i < ranges.length; i++) {
      const range = ranges[i]
      if (!range.empty()) {
        const from = range.from()
        const to = range.to()

        if (symbol !== 'Backspace') {
          const selection = cm.getRange(from, to)
          const anchorIndex = editor.indexFromPos(ranges[i].anchor)
          const headIndex = editor.indexFromPos(ranges[i].head)
          cm.replaceRange(symbol + selection + symbol, from, to, '+input')
          if (anchorIndex > headIndex) {
            ranges[i].anchor.ch += symbol.length
            ranges[i].head.ch += symbol.length
          } else {
            ranges[i].head.ch += symbol.length
            ranges[i].anchor.ch += symbol.length
          }
          cm.setSelections(ranges)
        } else {
          const preEndPos = {
            line: to.line,
            ch: to.ch + symbol.length
          }
          const preText = cm.getRange(to, preEndPos)
          const preIndex = wrapSymbols.indexOf(preText)
          const postEndPos = {
            line: from.line,
            ch: from.ch - symbol.length
          }
          const postText = cm.getRange(postEndPos, from)
          const postIndex = wrapSymbols.indexOf(postText)
          // check if surround symbol are list in array and matched
          if (preIndex > -1 && postIndex > -1 && preIndex === postIndex) {
            cm.replaceRange('', to, preEndPos, '+delete')
            cm.replaceRange('', postEndPos, from, '+delete')
          }
        }
      }
    }
  }
}

// Insert a CriticMarkup comment attributed to the current user (logged-in
// profile name or the fork's deterministic guest name). A selection is kept,
// wrapped in a highlight so the comment is anchored to it; never replaced.
// Without text, the cursor lands inside the empty comment.
export function insertCriticComment (cm, author, text) {
  if (!canInsertCritic(cm)) return false
  // author is a user-controlled display name; escape it too or a name like
  // `x<<}{>>@victim:` closes the span early and forges a second comment.
  const prefix = author ? '@' + escapeCriticText(author) + ': ' : ''
  const body = escapeCriticText(text || '')
  const comment = '{>>' + prefix + body + '<<}'
  const range = cm.listSelections()[0]
  const from = range.from()
  const to = range.to()
  const selection = cm.getRange(from, to)
  if (!selection) {
    insertText(cm, comment, 3 + prefix.length + body.length)
    return
  }
  // Selections holding highlight delimiters or blank lines (markdown-it
  // renders no inline span across blocks) get the comment appended unwrapped.
  const wrap = /\{==|==\}|\n\s*\n/.test(selection)
    ? selection + comment
    : '{==' + selection + '==}' + comment
  cm.replaceRange(wrap, from, to, '+input')
  cm.setCursor(cm.posFromIndex(cm.indexFromPos(from) + wrap.length))
  cm.focus()
}

// The toolbar acts on the first selection, matching the existing editor tools.
export function wrapCriticSuggestion (cm, type, insertedText) {
  if (!canInsertCritic(cm)) return false
  const range = cm.listSelections()[0]
  const source = cm.getValue()
  const plan = planCriticSuggestion(source, cm.indexFromPos(range.from()), cm.indexFromPos(range.to()), type, insertedText)
  if (plan.error) {
    if (window.showToast) window.showToast(plan.error, true)
    return false
  }
  cm.replaceRange(plan.text, cm.posFromIndex(plan.from), cm.posFromIndex(plan.to), '+input')
  const expected = source.slice(0, plan.from) + plan.text + source.slice(plan.to)
  // beforeChange enforces locked/read-only/disconnected mutation permissions.
  if (cm.getValue() !== expected) return false
  cm.setSelection(cm.posFromIndex(plan.selectionFrom), cm.posFromIndex(plan.selectionTo))
  return true
}

export function insertText (cm, text, cursorEnd = 0) {
  const cursor = cm.getCursor()
  cm.replaceSelection(text, cursor, cursor)
  cm.focus()
  cm.setCursor({ line: cursor.line, ch: cursor.ch + cursorEnd })
}

export function insertLink (cm, isImage) {
  const cursor = cm.getCursor()
  const ranges = cm.listSelections()
  const linkEnd = '](https://)'
  const symbol = (isImage) ? '![' : '['

  for (let i = 0; i < ranges.length; i++) {
    const range = ranges[i]
    if (!range.empty()) {
      const from = range.from()
      const to = range.to()
      const anchorIndex = editor.indexFromPos(ranges[i].anchor)
      const headIndex = editor.indexFromPos(ranges[i].head)
      let selection = cm.getRange(from, to)
      selection = symbol + selection + linkEnd
      cm.replaceRange(selection, from, to)
      if (anchorIndex > headIndex) {
        ranges[i].anchor.ch += symbol.length
        ranges[i].head.ch += symbol.length
      } else {
        ranges[i].head.ch += symbol.length
        ranges[i].anchor.ch += symbol.length
      }
      cm.setSelections(ranges)
    } else {
      cm.replaceRange(symbol + linkEnd, cursor, cursor)
      cm.setCursor({ line: cursor.line, ch: cursor.ch + symbol.length + linkEnd.length })
    }
  }
  cm.focus()
}

export function insertHeader (cm) {
  const cursor = cm.getCursor()
  const startOfLine = { line: cursor.line, ch: 0 }
  const startOfLineText = cm.getRange(startOfLine, { line: cursor.line, ch: 1 })
  // See if it is already a header
  if (startOfLineText === '#') {
    cm.replaceRange('#', startOfLine, startOfLine)
  } else {
    cm.replaceRange('# ', startOfLine, startOfLine)
  }
  cm.focus()
}

export function insertOnStartOfLines (cm, symbol) {
  const cursor = cm.getCursor()
  const ranges = cm.listSelections()

  for (let i = 0; i < ranges.length; i++) {
    const range = ranges[i]
    if (!range.empty()) {
      const cursorFrom = range.from()
      const cursorTo = range.to()
      const firstLineStart = { line: cursorFrom.line, ch: 0 }
      const lastLineEnd = { line: cursorTo.line, ch: cm.getLine(cursorTo.line).length }
      let selection = cm.getRange(firstLineStart, lastLineEnd)
      selection = selection.replace(/\n/g, '\n' + symbol)
      selection = symbol + selection
      cm.replaceRange(selection, firstLineStart, lastLineEnd)
    } else {
      cm.replaceRange(symbol, { line: cursor.line, ch: 0 }, { line: cursor.line, ch: 0 })
    }
  }
  cm.setCursor({ line: cursor.line, ch: cursor.ch + symbol.length })
  cm.focus()
}
